import React                from "react";
import Dialog               from "@mui/material/Dialog";
import Box                  from "@mui/material/Box";
import Container            from "@mui/material/Container";
import IconButton           from "@mui/material/IconButton";
import Typography           from "@mui/material/Typography";
import Close                from "@mui/icons-material/Close";
import ArrowBack            from "@mui/icons-material/ArrowBack";
import { useQueryClient }   from "react-query";
import Copyright            from "ui/components/Copyright";
import { HttpClient, ApiError } from "apiclient/ApiClient";
import CreateClassFields    from "ui/components/classes-components/CreateClassFields";
import AddStudentsFields    from "ui/components/classes-components/AddStudentsFields";
import ReviewStudentsList   from "ui/components/classes-components/ReviewStudentsList";
import { useProgramCatalog, gradeProgram } from "hooks/useProgramCatalog";
import {
    CREATE_CLASS_TITLE,
    ADD_STUDENTS_HEADER,
    ADD_STUDENTS_SUBHEADER,
    REVIEW_STUDENTS_HEADER,
    REVIEW_STUDENTS_SUBHEADER,
}                           from "config/config";

type Step = 'create' | 'add' | 'review';

export type StudentDraft = {
    key: number;       // stable row key within the flow (index at Add-submit time)
    name: string;
    program?: string;  // per-student override picked on Review (absent = class default)
};

// Sequential class-setup flow shared by both contexts — same windows, only the surround
// differs:
//   variant="dialog" → modal over the dashboard (× cancels; ← steps back from step 2)
//   variant="page"   → full-page over the empty onboarding background (no ×; ← from step 2)
// Steps: Create class → Add students → Review students (entry "create"),
//        or Add students → Review students (entry "add", existing classId + grade).
//
// Everything is collected client-side and sent as ONE request when Review is confirmed
// (review #340 item 1): nothing exists server-side before Confirm, so × simply closes —
// no cleanup DELETE, no orphaned students, no partial saves. Per-keystroke input state
// lives inside the step components; this flow only stores step-transition results, plus
// mutable draft refs (writing a ref doesn't rerender the dialog) so Back never loses
// typed-but-unsubmitted text (item 5).
export default function ClassFlow({
    open,
    initialStep,
    classId,
    grade,
    variant = 'dialog',
    onClose,
    onFinish,
}: {
    open: boolean;
    initialStep: 'create' | 'add';
    classId?: string;   // entry "add": the existing class
    grade?: string;     // entry "add": that class's grade (defaults Review's program picker)
    variant?: 'dialog' | 'page';
    onClose: () => void;
    onFinish: (classId: string) => void;
}) {
    const client = HttpClient();
    const queryClient = useQueryClient();
    const catalog = useProgramCatalog();
    const [step, setStep] = React.useState<Step>(initialStep);
    // Step-transition results — set on submit, never per keystroke.
    const [classDraft, setClassDraft] = React.useState<{ name: string; grade: string }>({ name: '', grade: '' });
    const [students, setStudents] = React.useState<StudentDraft[]>([]);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState('');
    // Latest unsubmitted input, mirrored by the step components on every change.
    // Refs on purpose: reading/writing them never rerenders this dialog.
    const createDraftRef = React.useRef<{ name: string; grade: string }>({ name: '', grade: '' });
    const namesDraftRef = React.useRef('');

    React.useEffect(() => {
        if (open) {
            setStep(initialStep);
            setClassDraft({ name: '', grade: '' });
            setStudents([]);
            setSaving(false);
            setSaveError('');
            createDraftRef.current = { name: '', grade: '' };
            namesDraftRef.current = '';
        }
    }, [open, initialStep]);

    const canGoBack = step !== initialStep;
    const goBack = () => {
        setSaveError('');
        if (step === 'review') setStep('add');
        else if (step === 'add') setStep('create');
    };

    // Re-submitting Add after Back rebuilds the roster from the textarea. Keep the
    // programs already picked on Review where the same name is still present (each
    // picked program is handed out once, so twins keep distinct overrides).
    const buildStudents = (names: string[], prev: StudentDraft[]): StudentDraft[] => {
        const carried = new Map<string, string[]>();
        prev.forEach((s) => {
            if (s.program) {
                const queue = carried.get(s.name) ?? [];
                queue.push(s.program);
                carried.set(s.name, queue);
            }
        });
        return names.map((name, idx) => {
            const program = carried.get(name)?.shift();
            return program ? { key: idx, name, program } : { key: idx, name };
        });
    };

    // The class grade in play: what the teacher picked (create flow) or the existing
    // class's grade (add flow). Defaults the Review picker via the catalog.
    const effectiveGrade = initialStep === 'create' ? classDraft.grade : grade;
    const defaultProgram = gradeProgram(catalog, effectiveGrade);

    // The ONE write of the whole flow — everything the teacher entered, atomically.
    const confirm = async (overrides: Record<number, string>) => {
        setSaving(true);
        setSaveError('');
        const payload = students.map((s) => {
            const program = overrides[s.key] ?? s.program;
            return program ? { name: s.name, program } : { name: s.name };
        });
        try {
            if (initialStep === 'create') {
                const createdId = await client.post<string>('/classes', {
                    name: classDraft.name,
                    grade: classDraft.grade,
                    students: payload,
                });
                queryClient.invalidateQueries('classes');
                onFinish(createdId as unknown as string);
            } else {
                await client.post(`/classes/${classId}/profiles`, { students: payload });
                queryClient.invalidateQueries('classes');
                onFinish(classId as string);
            }
        } catch (err) {
            setSaveError(err instanceof ApiError && err.message
                ? err.message
                : 'Something went wrong. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    let title = '';
    let subheader = '';
    let body: React.ReactNode = null;
    if (step === 'create') {
        title = CREATE_CLASS_TITLE;
        body = (
            <CreateClassFields
                initialName={createDraftRef.current.name}
                initialGrade={createDraftRef.current.grade}
                grades={catalog.grades}
                onDraft={(name, grade) => { createDraftRef.current = { name, grade }; }}
                onSubmit={(name, grade) => {
                    setClassDraft({ name, grade });
                    setStep('add');
                }}
            />
        );
    } else if (step === 'add') {
        title = ADD_STUDENTS_HEADER;
        subheader = ADD_STUDENTS_SUBHEADER;
        body = (
            <AddStudentsFields
                initialNames={namesDraftRef.current}
                onDraft={(names) => { namesDraftRef.current = names; }}
                onSubmit={(names) => {
                    setStudents((prev) => buildStudents(names, prev));
                    setStep('review');
                }}
            />
        );
    } else {
        title = REVIEW_STUDENTS_HEADER;
        subheader = REVIEW_STUDENTS_SUBHEADER;
        body = (
            <ReviewStudentsList
                students={students}
                defaultProgram={defaultProgram}
                programs={catalog.programs}
                saving={saving}
                error={saveError}
                onConfirm={confirm}
            />
        );
    }

    const chrome = (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 520,
                mx: 'auto',
                bgcolor: 'background.paper',
                borderRadius: 1,
                px: { xs: 3, sm: 8 },
                pt: 11,
                pb: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
            }}
        >
            {canGoBack &&
                <IconButton
                    aria-label="Back"
                    onClick={goBack}
                    sx={{ position: 'absolute', top: 32, left: { xs: 24, sm: 64 }, p: 0, color: 'secondary.main' }}
                >
                    <ArrowBack sx={{ fontSize: 24 }} />
                </IconButton>
            }
            {variant === 'dialog' &&
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 32, right: { xs: 24, sm: 64 }, p: 0, color: 'secondary.main' }}
                >
                    <Close sx={{ fontSize: 24 }} />
                </IconButton>
            }
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" textAlign="center">
                    {title}
                </Typography>
                {subheader &&
                    <Typography variant="body1" textAlign="center">
                        {subheader}
                    </Typography>
                }
            </Box>
            {body}
        </Box>
    );

    if (variant === 'page') {
        return (
            <>
                <Container component="main" maxWidth="sm" sx={{ mt: 9 }}>
                    {chrome}
                </Container>
                <Copyright sx={{ mt: 10, pb: 10 }} />
            </>
        );
    }

    return (
        <Dialog open={open} maxWidth={false} PaperProps={{ sx: { borderRadius: 2, width: 520, maxWidth: '100%' } }}>
            {chrome}
        </Dialog>
    );
}
